import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useShellStore, resolveEsaPanelRatio } from '../../../shell/store/shellStore';
import { getSolutionsCatalog } from '../../../shell/events/solutionsCatalogBridge';
import { buildSolutionCanvasMap } from '../../Esa/utils/solutionCanvasMap';
import headerData from '../data/globalHeaderData.json';
import type { GlobalHeaderData, WaffleItem } from '../data/types';

const data = headerData as GlobalHeaderData;
const BRIDGE_EVENT = '__SOLUTIONS_CATALOG_UPDATED__';

const ALL_ITEMS: Array<WaffleItem & { applicationId: string }> = data.applications.flatMap(app =>
  app.items.map(item => ({ ...item, applicationId: app.id })),
);

export interface ActiveModuleDisplay {
  name: string;
  icon: string | null;
}

function findItemByRoute(pathname: string): (WaffleItem & { applicationId: string }) | null {
  return ALL_ITEMS.find(item => item.route && pathname.startsWith(item.route)) ?? null;
}

function resolveCatalogDisplay(contextId: string): ActiveModuleDisplay | null {
  const snapshot = getSolutionsCatalog();
  if (!snapshot) return null;

  for (const item of snapshot.catalogItems) {
    const agent = item.runtime_agent;
    if (agent?.ukey === contextId) {
      return {
        name: agent.label ?? item.solution?.label ?? contextId,
        icon: 'Layers',
      };
    }
  }
  return null;
}

function resolveDisplay(contextId: string | null): ActiveModuleDisplay {
  if (!contextId) return { name: 'Select a module', icon: null };

  const staticMatch = ALL_ITEMS.find(i => i.id === contextId);
  if (staticMatch) return { name: staticMatch.name, icon: staticMatch.icon };

  const catalogMatch = resolveCatalogDisplay(contextId);
  if (catalogMatch) return catalogMatch;

  return { name: 'Select a module', icon: null };
}

export function useActiveModuleLabel(): ActiveModuleDisplay {
  const activeModule = useShellStore(state => state.activeModule);
  const activeSolution = useShellStore(state => state.activeSolution);
  const location = useLocation();
  const [, setCatalogVersion] = useState(0);

  const contextId = activeModule ?? activeSolution;

  const onBridgeUpdate = useCallback(() => {
    setCatalogVersion(v => v + 1);
  }, []);

  useEffect(() => {
    window.addEventListener(BRIDGE_EVENT, onBridgeUpdate);
    return () => window.removeEventListener(BRIDGE_EVENT, onBridgeUpdate);
  }, [onBridgeUpdate]);

  useEffect(() => {
    const matchedItem = findItemByRoute(location.pathname);
    if (!matchedItem) return;

    const { activeModule: curModule, activeSolution: curSolution } = useShellStore.getState();
    if (matchedItem.id === curModule || matchedItem.id === curSolution) return;

    // Waffle solution clicks set activeSolution then navigate to the canvas module
    // route. Do not overwrite that solution context with the static module id —
    // runs / catalog scoping depends on the selected runtime agent ukey.
    if (curSolution && matchedItem.type === 'module' && matchedItem.route) {
      const canvasMap = buildSolutionCanvasMap(getSolutionsCatalog());
      const canvasForSolution = canvasMap[curSolution];
      if (
        canvasForSolution &&
        matchedItem.route === `/operational/canvas/${canvasForSolution}`
      ) {
        return;
      }
    }

    const store = useShellStore.getState();
    const setContext =
      matchedItem.type === 'solution' ? store.setSolutionContext : store.setModuleContext;
    setContext(matchedItem.id, matchedItem.applicationId);
    store.setEsaPanelRatio(
      resolveEsaPanelRatio(
        matchedItem.type === 'module' ? matchedItem.id : null,
        matchedItem.type === 'solution' ? matchedItem.id : null,
      ),
    );
  }, [location.pathname]);

  return resolveDisplay(contextId);
}
